/*
 This file is part of GNU Taler
 (C) 2025-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { useTranslationContext } from "../../context/translation.js";
import { resolveFieldHandler, UIFormProps } from "../FormProvider.js";
import { FileFieldData } from "../forms-types.js";
import { LabelWithTooltipMaybeRequired } from "./InputLine.js";

export function InputFile(
  props: { maxBytes?: number; accept?: string } & UIFormProps<FileFieldData>,
): VNode {
  const { i18n } = useTranslationContext();
  const { label, tooltip, required, help, maxBytes, accept, disabled, hidden } =
    props;
  const { value, onChange, error } = resolveFieldHandler(
    props.handler,
    props.name,
  );
  const [fileError, setFileError] = useState<string>();

  if (hidden) return <Fragment />;

  const dataUri = !value
    ? undefined
    : `data:${value.MIME_TYPE ?? "application/octet-stream"};base64,${value.CONTENTS}`;

  function clearFile(): void {
    setFileError(undefined);
    onChange(undefined);
  }

  async function selectFile(file: File | undefined): Promise<void> {
    if (!file) {
      clearFile();
      return;
    }
    if (maxBytes !== undefined && file.size > maxBytes) {
      onChange(undefined);
      setFileError(i18n.str`The selected file is too large.`);
      return;
    }
    try {
      const contents = window.btoa(
        new Uint8Array(await file.arrayBuffer()).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );
      setFileError(undefined);
      onChange({
        CONTENTS: contents,
        ENCODING: "base64",
        FILENAME: file.name,
        MIME_TYPE: file.type,
      });
    } catch (readError) {
      console.error("Could not read selected file", readError);
      onChange(undefined);
      setFileError(i18n.str`The selected file could not be read.`);
    }
  }

  return (
    <div class="col-span-full">
      <LabelWithTooltipMaybeRequired
        label={label}
        technicalName={props.technicalName}
        tooltip={tooltip}
        required={required}
        name={String(props.name)}
      />
      {!value ? (
        <div class="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 py-1">
          <div class="text-center">
            <svg
              class="mx-auto h-12 w-12 text-gray-300"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                clip-rule="evenodd"
              />
            </svg>
            {!disabled && (
              <div class="my-2 flex text-sm leading-6 text-gray-600">
                <label
                  for={String(props.name)}
                  class="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                >
                  <span>{i18n.str`Upload a file`}</span>
                  <input
                    id={String(props.name)}
                    type="file"
                    class="sr-only"
                    accept={accept}
                    onChange={(event) => {
                      const files = event.currentTarget.files;
                      void selectFile(
                        files && files.length === 1 ? files[0] : undefined,
                      );
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Fragment>
          <div class="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 relative">
            {value.MIME_TYPE?.startsWith("image/") ? (
              <img
                src={dataUri}
                alt={value.FILENAME ?? ""}
                class="h-24 w-full object-cover relative"
              />
            ) : (
              <div class="h-24 w-full p-2">
                <div class="flex flex-row items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9Z"
                    />
                  </svg>
                  {value.FILENAME}
                </div>
              </div>
            )}
            {!disabled && (
              <button
                type="button"
                class="opacity-0 hover:opacity-70 duration-300 absolute rounded-lg border inset-0 z-10 flex justify-center text-xl items-center bg-black text-white cursor-pointer"
                onClick={clearFile}
              >
                {i18n.str`Clear`}
              </button>
            )}
          </div>
          <a
            class="font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
            href={dataUri}
            download={value.FILENAME}
          >
            {i18n.str`Download a copy.`}
          </a>
        </Fragment>
      )}
      {help && <p class="text-xs leading-5 text-gray-600 mt-2">{help}</p>}
      {(fileError || error) && (
        <p class="mt-2 text-sm text-red-600">{fileError ?? error}</p>
      )}
    </div>
  );
}
